/**
 * One-shot notices handed to /login through the query string.
 *
 * Some flows end by signing the user out and sending them to /login, which
 * leaves nowhere on the origin page to say what happened: changing a password
 * ends the session on purpose, so a banner rendered there has a few
 * milliseconds to live before the navigation. The message has to travel.
 *
 * A query flag rather than sessionStorage or a store: it survives the hard
 * navigation these flows use (`window.location.href`, chosen so nothing
 * rehydrates from memory), it needs no cleanup if the user never arrives, and
 * it is visible in the URL, which makes the behaviour obvious to the next
 * person reading it.
 *
 * Nothing here is trusted. The flag only decides which fixed sentence to show;
 * no value from the URL is rendered, so a hand-typed
 * `/login?passwordChanged=1` shows a harmless untrue confirmation and can do
 * nothing else. Never grow these into anything that carries content.
 */

/** Set after an in-app password change. See useProfilePage. */
export const PASSWORD_CHANGED_FLAG = 'passwordChanged';

/**
 * Set after an email was confirmed for an account made with "Sign up with
 * Microsoft". The Microsoft link is removed at that moment (so whoever claimed
 * the address from their own Microsoft account cannot get in), which leaves
 * password sign-in as the only way: /login says to use Forgot password.
 */
export const MICROSOFT_UNLINKED_FLAG = 'microsoftUnlinked';

/**
 * Why /login is being shown when the user did not ask to be there. Typed so a
 * hand-typed `?reason=` can only select one of these fixed sentences; the value
 * is never rendered. The wrong-audience case is deliberately absent: it routes
 * to /portal, not here.
 */
export const LOGIN_REASON_PARAM = 'reason';
export const SESSION_NOT_ESTABLISHED = 'session-not-established';
export type LoginReason = typeof SESSION_NOT_ESTABLISHED;

/** Narrow an untrusted search value to a known reason, or null. */
export function parseLoginReason(value: unknown): LoginReason | null {
  return value === SESSION_NOT_ESTABLISHED ? value : null;
}

/**
 * "Sign-in completed a moment ago" marker.
 *
 * A bounce to /login is ambiguous: a session that aged out and a session that
 * was never accepted look identical to the router. What tells them apart is
 * that, in the second case, the provider sign-in and POST /auth/gcp/session
 * JUST succeeded. So the sign-in handlers stamp a timestamp here right before
 * the hard navigation into the app, and the first successful profile load
 * clears it. If a gate or the 401 handler bounces to /login while the stamp is
 * still fresh, the session was not established; if there is no stamp, it is an
 * ordinary expiry and nothing is said.
 *
 * sessionStorage, not localStorage: it dies with the tab, so a stale stamp
 * cannot leak into a later visit. It holds a number, no identity, no token.
 * Every access is wrapped: storage can be blocked, and failing to leave a note
 * must never break sign-in or sign-out.
 */
const SIGN_IN_STAMP_KEY = 'biasafe.signInCompletedAt';

/** Longer than this and a bounce is an ordinary expiry, not a failed start. */
export const SIGN_IN_STAMP_TTL_MS = 60_000;

export function markSignInCompleted(): void {
  try {
    window.sessionStorage.setItem(SIGN_IN_STAMP_KEY, String(Date.now()));
  } catch {
    /* storage unavailable: the message is lost, sign-in is not */
  }
}

/** The app accepted the session: forget the stamp. */
export function clearSignInStamp(): void {
  try {
    window.sessionStorage.removeItem(SIGN_IN_STAMP_KEY);
  } catch {
    /* see above */
  }
}

/**
 * Read AND clear the stamp. True only when a sign-in completed within the TTL.
 * Cleared on read so one failure yields one message, and so the bounce to
 * /login (which has no stamp) cannot produce a second redirect.
 */
export function consumeSignInStamp(now: number = Date.now()): boolean {
  try {
    const raw = window.sessionStorage.getItem(SIGN_IN_STAMP_KEY);
    window.sessionStorage.removeItem(SIGN_IN_STAMP_KEY);
    if (!raw) return false;
    const age = now - Number(raw);
    return Number.isFinite(age) && age >= 0 && age <= SIGN_IN_STAMP_TTL_MS;
  } catch {
    return false;
  }
}

/** `search` for a redirect to /login: the reason when one applies, else none. */
export function loginRedirectSearch(): Record<string, string> | undefined {
  return consumeSignInStamp()
    ? { [LOGIN_REASON_PARAM]: SESSION_NOT_ESTABLISHED }
    : undefined;
}

/**
 * Developer hint: the stack expects https://app.biasafe.localhost, and a
 * Secure session cookie is dropped on plain-http localhost. Shown only on a
 * loopback host over a non-https page, so a real user never sees it.
 */
export function shouldShowLocalOriginHint(
  loc: Pick<Location, 'hostname' | 'protocol'> = window.location,
): boolean {
  return (
    (loc.hostname === 'localhost' || loc.hostname === '127.0.0.1') &&
    loc.protocol !== 'https:'
  );
}
